import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { validateComponente, hasErrors } from '@/utils/validation'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { componentiApi } from '@/api/box'
import { getFornitoriAttivi } from '@/api/products'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Form creazione/modifica componente scatola.
 * 
 * Features:
 * - Creazione nuovo componente
 * - Modifica componente esistente
 * - Validazione form
 * - Selezione fornitore
 */
export default function ComponenteForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = !!id

  // Form state
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    tipo: 'fondo',
    descrizione: '',
    costo_euro: '',
    larghezza_cm: '',
    altezza_cm: '',
    profondita_cm: '',
    peso_grammi: '',
    fornitore: '',
    attivo: true,
  })

  const [errors, setErrors] = useState({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Query componente esistente (solo in modifica)
  const { data: componenteData, isLoading: isLoadingComponente } = useQuery({
    queryKey: ['componente-scatola', id],
    queryFn: () => componentiApi.getById(id),
    enabled: isEditing,
  })

  // Query fornitori
  const { data: fornitoriData } = useQuery({
    queryKey: ['fornitori-attivi'],
    queryFn: getFornitoriAttivi,
  })

  const fornitori = fornitoriData?.data?.results || fornitoriData?.data || []

  // Popola form con dati esistenti
  useEffect(() => {
    if (componenteData?.data) {
      const c = componenteData.data
      setFormData({
        codice: c.codice || '',
        nome: c.nome || '',
        tipo: c.tipo || 'fondo',
        descrizione: c.descrizione || '',
        costo_euro: c.costo_cents != null ? (c.costo_cents / 100).toFixed(2) : '',
        larghezza_cm: c.larghezza_cm?.toString() || '',
        altezza_cm: c.altezza_cm?.toString() || '',
        profondita_cm: c.profondita_cm?.toString() || '',
        peso_grammi: c.peso_grammi?.toString() || '',
        fornitore: c.fornitore?.toString() || '',
        attivo: c.attivo ?? true,
      })
    }
  }, [componenteData])

  // Tipi componente
  const tipiComponente = [
    { value: 'fondo', label: 'Fondo' },
    { value: 'coperchio', label: 'Coperchio' },
    { value: 'fustella', label: 'Fustella' },
    { value: 'paglia', label: 'Paglia' },
    { value: 'nastro', label: 'Nastro' },
    { value: 'pellicola', label: 'Pellicola' },
    { value: 'sottovuoto', label: 'Sottovuoto' },
    { value: 'bustina', label: 'Bustina' },
    { value: 'coppetta', label: 'Coppetta' },
    { value: 'scatolina', label: 'Scatolina' },
    { value: 'adesivo', label: 'Adesivo' },
    { value: 'porta_lettera_a5', label: 'Porta Lettera A5' },
    { value: 'vasetto', label: 'Vasetto' },
    { value: 'cartone_spedizioni', label: 'Cartone Spedizioni' },
    { value: 'scotch', label: 'Scotch' },
    { value: 'altro', label: 'Altro' },
  ]

  // Mutation creazione
  const createMutation = useMutation({
    mutationFn: componentiApi.create,
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['componenti-scatola'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'], refetchType: 'all' }),
      ])
      toast.success('Componente creato con successo')
      navigate(`/box/componenti/${response.data.id}/modifica`)
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) {
        setErrors(errorData)
        Object.entries(errorData).forEach(([field, messages]) => {
          toast.error(`${field}: ${Array.isArray(messages) ? messages[0] : messages}`)
        })
      } else {
        toast.error('Errore durante la creazione')
      }
    },
  })

  // Mutation aggiornamento
  const updateMutation = useMutation({
    mutationFn: (data) => componentiApi.update(id, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['componenti-scatola'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['componente-scatola', id], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'], refetchType: 'all' }),
      ])
      toast.success('Componente aggiornato con successo')
      navigate('/box/componenti')
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) {
        setErrors(errorData)
        Object.entries(errorData).forEach(([field, messages]) => {
          toast.error(`${field}: ${Array.isArray(messages) ? messages[0] : messages}`)
        })
      } else {
        toast.error('Errore durante l\'aggiornamento')
      }
    },
  })

  // Mutation eliminazione
  const deleteMutation = useMutation({
    mutationFn: () => componentiApi.delete(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['componenti-scatola'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'], refetchType: 'all' }),
      ])
      toast.success('Componente eliminato con successo')
      navigate('/box/componenti')
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione')
    },
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Rimuovi errore quando l'utente modifica il campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validate = () => {
    const newErrors = validateComponente(formData)
    setErrors(newErrors)
    return !hasErrors(newErrors)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validate()) return

    const data = {
      nome: formData.nome.trim(),
      tipo: formData.tipo,
      descrizione: formData.descrizione.trim(),
      costo_cents: formData.costo_euro
        ? Math.round(parseFloat(formData.costo_euro) * 100)
        : 0,
      larghezza_cm: formData.larghezza_cm ? parseFloat(formData.larghezza_cm) : null,
      altezza_cm: formData.altezza_cm ? parseFloat(formData.altezza_cm) : null,
      profondita_cm: formData.profondita_cm ? parseFloat(formData.profondita_cm) : null,
      peso_grammi: formData.peso_grammi ? parseInt(formData.peso_grammi) : null,
      fornitore: formData.fornitore && formData.fornitore !== 'none' ? parseInt(formData.fornitore) : null,
      attivo: formData.attivo,
    }

    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  const formatData = (data) => {
    return new Date(data).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isEditing && isLoadingComponente) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/box/componenti')}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Modifica Componente' : 'Nuovo Componente'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? `Modifica i dati del componente ${componenteData?.data?.nome || ''}`
              : 'Inserisci i dati del nuovo componente scatola'
            }
          </p>
        </div>
        {isEditing && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Elimina
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Colonna principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dati Base */}
            <Card>
              <CardHeader>
                <CardTitle>Dati Base</CardTitle>
                <CardDescription>Informazioni principali del componente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codice">Codice (automatico)</Label>
                    <Input
                      id="codice"
                      value={formData.codice}
                      disabled
                      placeholder={isEditing ? '' : 'Verrà generato automaticamente'}
                    />
                    <p className="text-xs text-muted-foreground">
                      Generato automaticamente da tipo e nome al salvataggio.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo *</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => handleChange('tipo', value)}
                    >
                      <SelectTrigger className={errors.tipo ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Seleziona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {tipiComponente.map((tipo) => (
                          <SelectItem key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.tipo && (
                      <p className="text-sm text-red-500">{errors.tipo}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="Es: Fondo Scatola Grande"
                    className={errors.nome ? 'border-red-500' : ''}
                  />
                  {errors.nome && (
                    <p className="text-sm text-red-500">{errors.nome}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Textarea
                    id="descrizione"
                    value={formData.descrizione}
                    onChange={(e) => handleChange('descrizione', e.target.value)}
                    placeholder="Descrizione opzionale del componente..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Dimensioni */}
            <Card>
              <CardHeader>
                <CardTitle>Dimensioni e Peso</CardTitle>
                <CardDescription>Misure fisiche del componente (opzionali)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="larghezza_cm">Larghezza (cm)</Label>
                    <Input
                      id="larghezza_cm"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.larghezza_cm}
                      onChange={(e) => handleChange('larghezza_cm', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="altezza_cm">Lunghezza (cm)</Label>
                    <Input
                      id="altezza_cm"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.altezza_cm}
                      onChange={(e) => handleChange('altezza_cm', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profondita_cm">Altezza (cm)</Label>
                    <Input
                      id="profondita_cm"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.profondita_cm}
                      onChange={(e) => handleChange('profondita_cm', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="peso_grammi">Peso (g)</Label>
                    <Input
                      id="peso_grammi"
                      type="number"
                      min="0"
                      value={formData.peso_grammi}
                      onChange={(e) => handleChange('peso_grammi', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fornitore */}
            <Card>
              <CardHeader>
                <CardTitle>Fornitore</CardTitle>
                <CardDescription>Associa un fornitore al componente (opzionale)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="fornitore">Fornitore</Label>
                  <Select
                    value={formData.fornitore}
                    onValueChange={(value) => handleChange('fornitore', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona fornitore (opzionale)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun fornitore</SelectItem>
                      {fornitori.map((fornitore) => (
                        <SelectItem key={fornitore.id} value={fornitore.id.toString()}>
                          {fornitore.ragione_sociale}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonna laterale */}
          <div className="space-y-6">
            {/* Costo */}
            <Card>
              <CardHeader>
                <CardTitle>Costo</CardTitle>
                <CardDescription>Costo unitario del componente</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="costo_euro">Costo (€)</Label>
                  <Input
                    id="costo_euro"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.costo_euro}
                    onChange={(e) => handleChange('costo_euro', e.target.value)}
                    placeholder="Es: 1.50"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Stato */}
            <Card>
              <CardHeader>
                <CardTitle>Stato</CardTitle>
                <CardDescription>Visibilità del componente</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="attivo">Attivo</Label>
                    <p className="text-sm text-muted-foreground">
                      I componenti inattivi non sono selezionabili
                    </p>
                  </div>
                  <Switch
                    id="attivo"
                    checked={formData.attivo}
                    onCheckedChange={(checked) => handleChange('attivo', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Azioni */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending 
                      ? (isEditing ? 'Salvataggio...' : 'Creazione...') 
                      : (isEditing ? 'Salva Modifiche' : 'Crea Componente')
                    }
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/magazzino/movimenti')}
                  >
                    Storico Movimenti
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/box/componenti')}
                  >
                    Annulla
                  </Button>
                </div>
              </CardContent>
            </Card>

            {isEditing && componenteData?.data && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-muted-foreground" />
                    Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Creato il</p>
                      <p className="font-medium">{formatData(componenteData.data.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ultima modifica</p>
                      <p className="font-medium">{formatData(componenteData.data.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </form>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Componente</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il componente &quot;{componenteData?.data?.nome}&quot;?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
